import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutDashboard, Car as CarIcon, CalendarOff, MessageSquareQuote, Settings as SettingsIcon, Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useT } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";

const inputCls = "border border-input bg-background px-3 py-2 text-sm rounded-md w-full focus:outline-none focus:ring-2 focus:ring-accent";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block";
const emptyCar = { name: "", brand: "", category: "economy", transmission: "manual", fuel: "petrol", seats: 5, price_per_day: 0, features: "", images: "", description: "", status: "active" };

function CarDialog({ open, onClose, car, onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState(emptyCar);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (car) setForm({ ...car, features: (car.features || []).join(", "), images: (car.images || []).join(", ") });
    else setForm(emptyCar);
  }, [car, open]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, seats: Number(form.seats), price_per_day: Number(form.price_per_day),
        features: form.features.split(",").map((x) => x.trim()).filter(Boolean),
        images: form.images.split(",").map((x) => x.trim()).filter(Boolean) };
      if (car) await api.put(`/cars/${car.car_id}`, payload); else await api.post("/cars", payload);
      toast.success(t("admin.saved")); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{car ? t("admin.edit") : t("admin.add_car")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={labelCls}>{t("admin.name")}</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="car-form-name" /></div>
          <div><label className={labelCls}>{t("admin.brand")}</label><input className={inputCls} value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="car-form-brand" /></div>
          <div><label className={labelCls}>{t("admin.category")}</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="rounded-md" data-testid="car-form-category"><SelectValue /></SelectTrigger>
              <SelectContent>{["economy", "compact", "suv", "premium", "van", "electric"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><label className={labelCls}>{t("admin.price")}</label><input type="number" className={inputCls} value={form.price_per_day} onChange={(e) => setForm({ ...form, price_per_day: e.target.value })} data-testid="car-form-price" /></div>
          <div><label className={labelCls}>{t("car.transmission")}</label>
            <Select value={form.transmission} onValueChange={(v) => setForm({ ...form, transmission: v })}>
              <SelectTrigger className="rounded-md"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="manual">{t("fleet.manual")}</SelectItem><SelectItem value="automatic">{t("fleet.automatic")}</SelectItem></SelectContent>
            </Select></div>
          <div><label className={labelCls}>{t("car.fuel")}</label>
            <Select value={form.fuel} onValueChange={(v) => setForm({ ...form, fuel: v })}>
              <SelectTrigger className="rounded-md"><SelectValue /></SelectTrigger>
              <SelectContent>{["petrol", "diesel", "electric", "hybrid"].map((f) => <SelectItem key={f} value={f}>{t(`fleet.${f}`)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><label className={labelCls}>{t("admin.seats")}</label><input type="number" className={inputCls} value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} /></div>
          <div><label className={labelCls}>Status</label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="rounded-md"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">active</SelectItem><SelectItem value="service">service</SelectItem><SelectItem value="unavailable">unavailable</SelectItem></SelectContent>
            </Select></div>
          <div className="col-span-2"><label className={labelCls}>{t("admin.features_csv")}</label><input className={inputCls} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} data-testid="car-form-features" /></div>
          <div className="col-span-2"><label className={labelCls}>{t("admin.images_csv")}</label><input className={inputCls} value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} data-testid="car-form-images" /></div>
          <div className="col-span-2"><label className={labelCls}>{t("admin.description")}</label><textarea className={inputCls} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-md" onClick={onClose}>{t("admin.cancel")}</Button>
          <Button className="rounded-md bg-accent hover:bg-accent/90" onClick={save} disabled={saving} data-testid="car-form-save">{t("admin.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettingsTab({ qc }) {
  const { t } = useT();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data && !form) setForm(data); }, [data]);
  if (!form) return <p className="text-muted-foreground">{t("common.loading")}</p>;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setWhy = (i, k, v) => { const arr = [...(form.why_us || [])]; arr[i] = { ...arr[i], [k]: v }; set("why_us", arr); };
  const addWhy = () => set("why_us", [...(form.why_us || []), { title: "", text: "" }]);
  const removeWhy = (i) => set("why_us", form.why_us.filter((_, idx) => idx !== i));

  const setExtra = (i, k, v) => { const arr = [...(form.extras || [])]; arr[i] = { ...arr[i], [k]: k === "price" ? Number(v) : v }; set("extras", arr); };
  const addExtra = () => set("extras", [...(form.extras || []), { label: "", price: 0 }]);
  const removeExtra = (i) => set("extras", form.extras.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", form); qc.invalidateQueries({ queryKey: ["settings"] }); toast.success(t("admin.saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const Field = ({ k, label }) => (
    <div><label className={labelCls}>{label}</label><input className={inputCls} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`settings-${k}`} /></div>
  );

  const section = "border border-border rounded-lg p-5";
  const title = "font-heading font-semibold mb-4";

  return (
    <div className="space-y-6">
      <div className={section}>
        <h3 className={title}>{t("admin.s_content")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field k="company_name" label={t("admin.company_name")} /><Field k="tagline" label={t("admin.tagline")} />
          <Field k="hero_title" label={t("admin.hero_title")} /><Field k="hero_subtitle" label={t("admin.hero_subtitle")} />
        </div>
      </div>
      <div className={section}>
        <h3 className={title}>{t("admin.s_contact")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field k="phone" label={t("admin.phone")} /><Field k="whatsapp" label={t("admin.whatsapp")} />
          <Field k="email" label="Email" /><Field k="program" label={t("admin.program")} />
          <Field k="address" label={t("admin.address")} />
        </div>
      </div>
      <div className={section}>
        <h3 className={title}>{t("admin.s_legal")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field k="cui" label={t("admin.cui")} /><Field k="reg_com" label={t("admin.reg_com")} /><Field k="sediu_social" label={t("admin.sediu")} />
        </div>
      </div>
      <div className={section}>
        <h3 className={title}>{t("admin.s_social")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field k="facebook" label="Facebook" /><Field k="instagram" label="Instagram" /><Field k="tiktok" label="TikTok" />
        </div>
      </div>
      <div className={section}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold">{t("admin.s_why")}</h3>
          <Button variant="outline" size="sm" className="rounded-md gap-1" onClick={addWhy} data-testid="add-why"><Plus className="h-3 w-3" /> {t("admin.add")}</Button>
        </div>
        <div className="space-y-3">
          {(form.why_us || []).map((w, i) => (
            <div key={i} className="flex gap-3 items-start">
              <input className={inputCls} placeholder="Titlu" value={w.title} onChange={(e) => setWhy(i, "title", e.target.value)} />
              <input className={inputCls} placeholder="Text" value={w.text} onChange={(e) => setWhy(i, "text", e.target.value)} />
              <Button variant="ghost" size="sm" onClick={() => removeWhy(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          ))}
        </div>
      </div>
      <div className={section}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold">{t("admin.s_extras")}</h3>
          <Button variant="outline" size="sm" className="rounded-md gap-1" onClick={addExtra} data-testid="add-extra"><Plus className="h-3 w-3" /> {t("admin.add")}</Button>
        </div>
        <div className="space-y-3">
          {(form.extras || []).map((ex, i) => (
            <div key={i} className="flex gap-3 items-start" data-testid={`extra-row-${i}`}>
              <input className={inputCls} placeholder={t("admin.extra_label")} value={ex.label} onChange={(e) => setExtra(i, "label", e.target.value)} data-testid={`extra-label-${i}`} />
              <input type="number" className={`${inputCls} max-w-[140px]`} placeholder={t("admin.extra_price")} value={ex.price} onChange={(e) => setExtra(i, "price", e.target.value)} data-testid={`extra-price-${i}`} />
              <Button variant="ghost" size="sm" onClick={() => removeExtra(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          ))}
        </div>
      </div>
      <Button className="rounded-md bg-accent hover:bg-accent/90" onClick={save} disabled={saving} data-testid="settings-save">{t("admin.save")}</Button>
    </div>
  );
}

function AvailabilityTab({ qc, cars }) {
  const { t } = useT();
  const { data: blocks = [] } = useQuery({ queryKey: ["admin-blocks"], queryFn: async () => (await api.get("/admin/blocks")).data });
  const [form, setForm] = useState({ car_id: "", date_from: "", date_to: "", label: "" });

  const add = async () => {
    if (!form.car_id || !form.date_from || !form.date_to) { toast.error("Completează câmpurile"); return; }
    try { await api.post("/admin/blocks", form); qc.invalidateQueries({ queryKey: ["admin-blocks"] }); setForm({ car_id: "", date_from: "", date_to: "", label: "" }); toast.success(t("admin.saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/admin/blocks/${id}`); qc.invalidateQueries({ queryKey: ["admin-blocks"] }); };

  return (
    <div className="space-y-6">
      <div className="border border-border rounded-lg p-5 grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
        <div><label className={labelCls}>{t("admin.block_car")}</label>
          <Select value={form.car_id} onValueChange={(v) => setForm({ ...form, car_id: v })}>
            <SelectTrigger className="rounded-md" data-testid="block-car"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{cars.map((c) => <SelectItem key={c.car_id} value={c.car_id}>{c.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><label className={labelCls}>{t("admin.block_from")}</label><input type="datetime-local" className={inputCls} value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} data-testid="block-from" /></div>
        <div><label className={labelCls}>{t("admin.block_to")}</label><input type="datetime-local" className={inputCls} value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} data-testid="block-to" /></div>
        <div><label className={labelCls}>{t("admin.block_label")}</label><input className={inputCls} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="block-label" /></div>
        <Button className="rounded-md bg-accent hover:bg-accent/90 gap-1" onClick={add} data-testid="add-block"><Plus className="h-4 w-4" /> {t("admin.add_block")}</Button>
      </div>
      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>{t("admin.block_car")}</TableHead><TableHead>{t("admin.block_from")}</TableHead><TableHead>{t("admin.block_to")}</TableHead><TableHead>{t("admin.block_label")}</TableHead><TableHead className="text-right">{t("admin.actions")}</TableHead></TableRow></TableHeader>
          <TableBody>
            {blocks.map((b) => (
              <TableRow key={b.block_id} data-testid={`block-row-${b.block_id}`}>
                <TableCell className="font-medium">{b.car_name}</TableCell>
                <TableCell className="text-xs">{new Date(b.date_from).toLocaleString()}</TableCell>
                <TableCell className="text-xs">{new Date(b.date_to).toLocaleString()}</TableCell>
                <TableCell>{b.label || "—"}</TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => remove(b.block_id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function TestimonialsTab({ qc }) {
  const { t } = useT();
  const { data: items = [] } = useQuery({ queryKey: ["testimonials"], queryFn: async () => (await api.get("/testimonials")).data });
  const [form, setForm] = useState({ name: "", text: "", rating: 5, location: "", avatar: "" });

  const add = async () => {
    if (!form.name || !form.text) { toast.error("Completează câmpurile"); return; }
    try { await api.post("/testimonials", { ...form, rating: Number(form.rating) }); qc.invalidateQueries({ queryKey: ["testimonials"] }); setForm({ name: "", text: "", rating: 5, location: "", avatar: "" }); toast.success(t("admin.saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/testimonials/${id}`); qc.invalidateQueries({ queryKey: ["testimonials"] }); };

  return (
    <div className="space-y-6">
      <div className="border border-border rounded-lg p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>{t("admin.t_name")}</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="t-name" /></div>
        <div><label className={labelCls}>{t("admin.t_location")}</label><input className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
        <div className="sm:col-span-2"><label className={labelCls}>{t("admin.t_text")}</label><textarea className={inputCls} rows={2} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="t-text" /></div>
        <div><label className={labelCls}>{t("admin.t_rating")}</label><input type="number" min="1" max="5" className={inputCls} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></div>
        <div><label className={labelCls}>{t("admin.t_avatar")}</label><input className={inputCls} value={form.avatar} onChange={(e) => setForm({ ...form, avatar: e.target.value })} /></div>
        <div className="sm:col-span-2"><Button className="rounded-md bg-accent hover:bg-accent/90 gap-1" onClick={add} data-testid="add-testimonial"><Plus className="h-4 w-4" /> {t("admin.add")}</Button></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((tm) => (
          <div key={tm.testimonial_id} className="border border-border rounded-lg p-5" data-testid={`testimonial-row-${tm.testimonial_id}`}>
            <div className="flex items-start justify-between">
              <div className="flex gap-0.5 mb-2">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < tm.rating ? "fill-accent text-accent" : "text-border"}`} />)}</div>
              <Button variant="ghost" size="sm" onClick={() => remove(tm.testimonial_id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
            <p className="text-sm mb-3">"{tm.text}"</p>
            <p className="text-sm font-semibold">{tm.name}</p><p className="text-xs text-muted-foreground">{tm.location}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const { t } = useT();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState("dashboard");
  const [carDialog, setCarDialog] = useState({ open: false, car: null });

  const enabled = !!user && user.role === "admin";
  const { data: stats } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data, enabled });
  const { data: cars = [] } = useQuery({ queryKey: ["admin-cars"], queryFn: async () => (await api.get("/cars", { params: { admin: true } })).data, enabled });

  useEffect(() => { if (!loading && (!user || user.role !== "admin")) navigate("/login"); }, [loading, user, navigate]);
  if (loading) return <div className="max-w-6xl mx-auto px-6 py-20 text-muted-foreground">{t("common.loading")}</div>;
  if (!user || user.role !== "admin") return null;

  const deleteCar = async (id) => { await api.delete(`/cars/${id}`); toast.success("OK"); qc.invalidateQueries({ queryKey: ["admin-cars"] }); };

  const navItems = [
    { key: "dashboard", label: t("admin.dashboard"), icon: LayoutDashboard },
    { key: "fleet", label: t("admin.fleet"), icon: CarIcon },
    { key: "availability", label: t("admin.availability"), icon: CalendarOff },
    { key: "testimonials", label: t("admin.testimonials"), icon: MessageSquareQuote },
    { key: "settings", label: t("admin.settings"), icon: SettingsIcon },
  ];

  const statCards = stats ? [
    { label: t("admin.total_cars"), value: stats.total_cars },
    { label: t("admin.active_cars"), value: stats.active_cars },
    { label: t("admin.service_cars"), value: stats.service_cars },
    { label: t("admin.total_blocks"), value: stats.total_blocks },
    { label: t("admin.testimonials"), value: stats.total_testimonials },
  ] : [];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-8">{t("admin.title")}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <aside className="lg:col-span-1">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto">
            {navItems.map((item) => (
              <button key={item.key} onClick={() => setTab(item.key)} data-testid={`admin-tab-${item.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${tab === item.key ? "bg-black text-white" : "hover:bg-secondary"}`}>
                <item.icon className="h-4 w-4" /> {item.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="lg:col-span-4">
          {tab === "dashboard" && stats && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {statCards.map((c, i) => (
                <div key={i} className="border border-border rounded-lg p-5" data-testid={`stat-${i}`}>
                  <p className="font-heading text-3xl font-bold">{c.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{c.label}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "fleet" && (
            <div>
              <div className="flex justify-between items-center mb-5">
                <h2 className="font-heading font-semibold text-xl">{t("admin.fleet")}</h2>
                <Button className="rounded-md bg-accent hover:bg-accent/90 gap-2" onClick={() => setCarDialog({ open: true, car: null })} data-testid="admin-add-car"><Plus className="h-4 w-4" /> {t("admin.add_car")}</Button>
              </div>
              <div className="border border-border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader><TableRow><TableHead>{t("admin.name")}</TableHead><TableHead>{t("admin.category")}</TableHead><TableHead>{t("admin.price")}</TableHead><TableHead>Status</TableHead><TableHead className="text-right">{t("admin.actions")}</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {cars.map((c) => (
                      <TableRow key={c.car_id} data-testid={`admin-car-row-${c.car_id}`}>
                        <TableCell className="font-medium">{c.name}</TableCell>
                        <TableCell className="capitalize">{c.category}</TableCell>
                        <TableCell>{c.price_per_day} lei</TableCell>
                        <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${c.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-secondary"}`}>{c.status}</span></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => setCarDialog({ open: true, car: c })} data-testid={`edit-car-${c.car_id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteCar(c.car_id)} data-testid={`delete-car-${c.car_id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {tab === "availability" && <AvailabilityTab qc={qc} cars={cars} />}
          {tab === "testimonials" && <TestimonialsTab qc={qc} />}
          {tab === "settings" && <SettingsTab qc={qc} />}
        </div>
      </div>

      <CarDialog open={carDialog.open} car={carDialog.car} onClose={() => setCarDialog({ open: false, car: null })} onSaved={() => qc.invalidateQueries({ queryKey: ["admin-cars"] })} />
    </div>
  );
}
